import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const aiBlocks = [
  {
    id: 'chimney',
    icon: 'ri-fire-fill',
    tag: 'What is chimney cleaning?',
    title: 'Chimney Cleaning & Why It Matters',
    answer: 'Chimney cleaning removes creosote, soot, blockages, and debris from your chimney flue. Creosote — a byproduct of burning wood — is highly flammable and is the leading cause of chimney fires. The CSIA recommends annual chimney inspections and cleaning for any chimney that is used regularly.',
    stats: [{ value: '2,900+', label: 'Chimney fires in US homes annually' }, { value: '86%', label: 'Caused by creosote buildup' }],
    link: '/services/chimney-cleaning',
    linkText: 'Learn About Chimney Cleaning',
    image: 'https://public.readdy.ai/ai/img_res/95b8dd185075a481b4e8e5f471a81c15.jpg',
  },
  {
    id: 'duct',
    icon: 'ri-lungs-fill',
    tag: 'Benefits of air duct cleaning',
    title: 'Air Duct Cleaning & Indoor Air Quality',
    answer: 'Air duct cleaning removes accumulated dust, pet dander, mold spores, allergens, and contaminants from your HVAC system. The EPA notes that indoor air can be 2–5 times more polluted than outdoor air. Clean ducts improve air quality, reduce allergy symptoms, and help your HVAC system run more efficiently — saving energy costs.',
    stats: [{ value: '5x', label: 'Indoor air more polluted than outdoors' }, { value: '25%', label: 'Improved HVAC efficiency after cleaning' }],
    link: '/services/air-duct-cleaning',
    linkText: 'Learn About Air Duct Cleaning',
    image: 'https://readdy.ai/api/search-image?query=HVAC%20air%20duct%20interior%20showing%20accumulated%20dust%20and%20debris%20before%20cleaning%2C%20dark%20duct%20walls%20with%20lint%20and%20particulate%20buildup%2C%20educational%20home%20maintenance%20concept%2C%20macro%20photography%2C%20technical%20cross%20section%20view&width=500&height=380&seq=ai_duct_v1&orientation=landscape',
  },
  {
    id: 'dryer',
    icon: 'ri-windy-fill',
    tag: 'Why dryer vent cleaning is critical',
    title: 'Dryer Vent Cleaning & Fire Prevention',
    answer: 'The U.S. Fire Administration reports nearly 2,900 home fires each year are caused by clothes dryers — the majority from clogged vents. Lint buildup restricts airflow and can ignite. Signs you need cleaning: clothes taking multiple cycles to dry, the dryer running hot, a burning smell, or it\'s been over a year since your last cleaning.',
    stats: [{ value: '2,900+', label: 'Dryer fires annually in the US' }, { value: '34%', label: 'Of fires caused by lint buildup' }],
    link: '/services/dryer-vent-cleaning',
    linkText: 'Learn About Dryer Vent Cleaning',
    image: 'https://readdy.ai/api/search-image?query=dryer%20vent%20duct%20filled%20with%20dangerous%20lint%20buildup%20close-up%20macro%20photo%20showing%20fire%20hazard%2C%20white%20exhaust%20hose%20clogged%20with%20lint%20and%20dust%2C%20home%20safety%20awareness%20concept%2C%20high%20contrast%20clear%20photography&width=500&height=380&seq=ai_dryer_v1&orientation=landscape',
  },
];

export default function AIAnswerSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-28 bg-white" id="learn">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-4">
            <div className="h-px w-8 bg-brand-amber" />
            Know the Facts
            <div className="h-px w-8 bg-brand-amber" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark mb-4">
            Why These 3 Services<br />
            <span className="text-brand-amber">Are Essential for Every Home</span>
          </h2>
          <p className="text-brand-warm-gray text-lg max-w-2xl mx-auto">
            Chimney cleaning, air duct cleaning, and dryer vent cleaning aren't just maintenance — they're critical home safety services that protect your family.
          </p>
        </div>

        {/* Answer blocks */}
        <div className="space-y-14">
          {aiBlocks.map((block, index) => (
            <div
              key={block.id}
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
            >
              {/* Image */}
              <div className="lg:w-[42%] flex-shrink-0">
                <div className="rounded-3xl overflow-hidden aspect-[4/3]">
                  <img
                    src={block.image}
                    alt={block.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="lg:w-[58%]">
                {/* Tag */}
                <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full mb-5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${block.icon} text-brand-amber`} />
                  </div>
                  {block.tag}
                </div>

                <h3 className="font-serif text-3xl lg:text-4xl font-bold text-brand-dark mb-5 leading-tight">
                  {block.title}
                </h3>

                {/* AI-optimized answer */}
                <p className="text-brand-warm-gray text-base leading-relaxed mb-7">
                  {block.answer}
                </p>

                {/* Stats */}
                <div className="flex gap-4 mb-8">
                  {block.stats.map((stat) => (
                    <div key={stat.label} className="flex-1 bg-stone-50 border border-stone-100 rounded-2xl p-5 text-center">
                      <div className="font-serif text-2xl font-bold text-brand-amber mb-1">{stat.value}</div>
                      <div className="text-brand-warm-gray text-xs leading-snug">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <Link
                  to={block.link}
                  className="inline-flex items-center gap-2 bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3.5 px-8 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  {block.linkText}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
